import { T as TokenApi, a as TokenService } from './token-service-Ba9fp95X.js';

/**
 * Token SDK - Class-based Promise SDK for Token Operations
 *
 * Modern, performant SDK with direct method access and excellent TypeScript support.
 * Optimized for bundle size, performance, and developer experience.
 *
 * Usage:
 *   const tokenSDK = new TokenSDK(apiClient);
 *   const tokens = await tokenSDK.getTokens();
 *   const creditToken = await tokenSDK.getActiveCreditToken();
 */
class TokenSDK {
    constructor(apiClient) {
        this.tokenApi = new TokenApi(apiClient);
        this.tokenService = new TokenService(this.tokenApi);
    }
    // ==========================================
    // CONVENIENCE METHODS - Direct Promise API
    // ==========================================
    // Simplified method names for better developer experience
    /**
     * Get all tokens
     * @returns Promise with all available tokens
     */
    async getTokens() {
        return this.tokenService.getRemoteTokens();
    }
    /**
     * Get all token types
     * @returns Promise with all available token types
     */
    async getTokenTypes() {
        return this.tokenService.getRemoteTokenTypes();
    }
    /**
     * Get the active credit token
     * @returns Promise with the current active credit token
     */
    async getActiveCreditToken() {
        return this.tokenService.getRemoteActiveCreditToken();
    }
    /**
     * Get all reward tokens
     * @returns Promise with all reward tokens
     */
    async getRewardTokens() {
        return this.tokenService.getRemoteRewardTokens();
    }
    /**
     * Get all status tokens
     * @returns Promise with all status tokens
     */
    async getStatusTokens() {
        return this.tokenService.getRemoteStatusTokens();
    }
    /**
     * Get token by contract address
     * @param contractAddress - The contract address to search for
     * @param contractTokenId - Optional contract token ID
     * @returns Promise with the matching token
     */
    async getTokenByContract(contractAddress, contractTokenId = null) {
        return this.tokenService.getTokenByContractAddress(contractAddress, contractTokenId);
    }
    // ==========================================
    // ADMIN METHODS - Token Management
    // ==========================================
    /**
     * Create token metadata
     * @param tokenId - The token ID
     * @param tokenData - The token metadata request data
     * @returns Promise with the updated token
     */
    async createTokenMetadata(tokenId, tokenData) {
        return this.tokenService.createTokenMetadata(tokenId, tokenData);
    }
    /**
     * Update token metadata (ERC721 only)
     * Note: Existing minted NFTs retain their original metadata - this only affects future mints
     * @param metadataId - The metadata ID to update
     * @param tokenData - The token metadata request data
     * @returns Promise with the updated metadata
     */
    async updateTokenMetadata(metadataId, tokenData) {
        return this.tokenService.updateTokenMetadata(metadataId, tokenData);
    }
    /**
     * Toggle token active status
     * @param tokenId - The token ID to toggle
     * @returns Promise with the updated token
     */
    async toggleTokenActive(tokenId) {
        return this.tokenService.toggleTokenActive(tokenId);
    }
    /**
     * Create a new token
     * @param tokenData - The token creation data
     * @returns Promise with the created token
     */
    async createToken(tokenData) {
        return this.tokenService.createToken(tokenData);
    }
    /**
     * Update an existing token
     * @param tokenId - The token ID to update
     * @param tokenData - The token update data
     * @returns Promise with the updated token
     */
    async updateToken(tokenId, tokenData) {
        return this.tokenService.updateToken(tokenId, tokenData);
    }
    /**
     * Set mainnet contract address for a token
     * @param tokenId - The token ID
     * @param contractAddress - The contract address
     * @param chainId - The blockchain chain ID
     * @returns Promise with the updated token
     */
    async setMainnetContract(tokenId, contractAddress, chainId) {
        return this.tokenService.setMainnetContract(tokenId, contractAddress, chainId);
    }
    /**
     * Toggle token metadata status
     * @param metadataId - The metadata ID to toggle
     * @returns Promise with the updated metadata
     */
    async toggleTokenMetadataStatus(metadataId) {
        return this.tokenService.toggleTokenMetadataStatus(metadataId);
    }
    /**
     * Create a new token type
     * @param tokenType - The token type data
     * @returns Promise with the created token type
     */
    async createTokenType(tokenType) {
        return this.tokenService.createTokenType(tokenType);
    }
    // ==========================================
    // ADVANCED ACCESS - For Complex Operations
    // ==========================================
    /**
     * Get direct access to the token service for advanced operations
     * @returns The underlying TokenService instance
     */
    getTokenService() {
        return this.tokenService;
    }
    /**
     * Get direct access to the token API for low-level operations
     * @returns The underlying TokenApi instance
     */
    getTokenApi() {
        return this.tokenApi;
    }
}

// ==========================================
// TOKEN UTILITY FUNCTIONS
// ==========================================
/**
 * Get metadata from a token unit response
 * Handles metadata-based tokens (ERC-1155 with metadata array)
 *
 * For metadata-based tokens:
 * - Returns the metadata object from token.metadata array
 * - Uses provided incrementalId or tokenMetadataIncrementalId from tokenUnit
 * - Defaults to index 0 if no incrementalId specified
 *
 * For standard tokens (no metadata):
 * - Returns null
 *
 * @param tokenUnit - The token unit from campaign or balance
 * @param incrementalId - Optional metadata index to use (overrides tokenUnit.tokenMetadataIncrementalId)
 * @returns Token metadata object or null
 *
 * @example
 * ```typescript
 * // Get metadata using tokenUnit's incrementalId
 * const tokenUnit = {
 *   tokenMetadataIncrementalId: 3,
 *   token: {
 *     metadata: [
 *       { name: 'Bronze Pass', imageUrl: '...' },
 *       { name: 'Silver Pass', imageUrl: '...' },
 *       { name: 'Gold Pass', imageUrl: '...' }
 *     ]
 *   }
 * };
 * const metadata = getMetadataFromTokenUnitResponse(tokenUnit);
 * // Returns: { name: 'Bronze Pass', imageUrl: '...' } (index 0 - default)
 *
 * // Override incrementalId
 * const metadata2 = getMetadataFromTokenUnitResponse(tokenUnit, 2);
 * // Returns: { name: 'Gold Pass', imageUrl: '...' } (index 2)
 *
 * // Standard token (no metadata)
 * const tokenUnit2 = {
 *   token: { symbol: 'VQP', name: 'Visit Qatar Points' }
 * };
 * const metadata3 = getMetadataFromTokenUnitResponse(tokenUnit2);
 * // Returns: null
 *
 * // Display name with fallback
 * const displayName = metadata?.name || tokenUnit.token?.symbol || 'Reward';
 *
 * // Display image
 * const imageUrl = metadata?.imageUrl;
 * ```
 */
function getMetadataFromTokenUnitResponse(tokenUnit, incrementalId) {
    // If no metadata array exists, return null
    if (!tokenUnit.token?.metadata || tokenUnit.token.metadata.length === 0) {
        return null;
    }
    const metadata = tokenUnit.token.metadata;
    // Use provided incrementalId or tokenUnit's incrementalId to find matching metadata
    const targetId = incrementalId ?? tokenUnit.tokenMetadataIncrementalId;
    if (targetId !== undefined) {
        const found = metadata.find((m) => m.incrementalId === targetId);
        if (found)
            return found;
    }
    // Fallback to first metadata item
    return metadata[0];
}

/**
 * Abstract Base Token Service - Explicit Initialization Pattern
 *
 * Platform-agnostic token operations with Promise-based API.
 * Framework services extend this and control initialization lifecycle.
 *
 * Benefits:
 * - Explicit initialization control
 * - Better error boundaries
 * - Clear lifecycle management
 * - Testable initialization state
 * - Zero framework dependencies
 */
class BaseTokenService {
    constructor() {
        this._isInitialized = false;
    }
    // ==========================================
    // INITIALIZATION LIFECYCLE
    // ==========================================
    /**
     * LIFECYCLE: Initialize token service with API client
     * Must be called before using any token operations
     */
    initializeTokenService(apiClient) {
        if (!apiClient) {
            throw new Error('Cannot initialize TokenService: apiClient is null or undefined');
        }
        if (!this._isInitialized) {
            this._tokenApi = new TokenApi(apiClient);
            this._tokenBusinessService = new TokenService(this._tokenApi);
            this._isInitialized = true;
        }
    }
    /**
     * LIFECYCLE: Check if token service is initialized
     */
    get isTokenServiceInitialized() {
        return this._isInitialized;
    }
    /**
     * INTERNAL: Get token business service (throws if not initialized)
     */
    get tokenBusinessService() {
        if (!this._tokenBusinessService) {
            throw new Error('TokenService not initialized. Call initializeTokenService(apiClient) first.');
        }
        return this._tokenBusinessService;
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get all remote tokens
     */
    getRemoteTokens() {
        return this.tokenBusinessService.getRemoteTokens();
    }
    /**
     * PUBLIC: Get all remote token types
     */
    getRemoteTokenTypes() {
        return this.tokenBusinessService.getRemoteTokenTypes();
    }
    /**
     * PUBLIC: Get active credit token
     */
    getRemoteActiveCreditToken() {
        return this.tokenBusinessService.getRemoteActiveCreditToken();
    }
    /**
     * PUBLIC: Get reward tokens
     */
    getRemoteRewardTokens() {
        return this.tokenBusinessService.getRemoteRewardTokens();
    }
    /**
     * PUBLIC: Get status tokens
     */
    getRemoteStatusTokens() {
        return this.tokenBusinessService.getRemoteStatusTokens();
    }
    /**
     * PUBLIC: Get token by contract address
     */
    getTokenByContractAddress(contractAddress, contractTokenId) {
        return this.tokenBusinessService.getTokenByContractAddress(contractAddress, contractTokenId);
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Create token metadata
     */
    createTokenMetadata(tokenId, tokenData) {
        return this.tokenBusinessService.createTokenMetadata(tokenId, tokenData);
    }
    /**
     * ADMIN: Toggle token active status
     */
    toggleTokenActive(tokenId) {
        return this.tokenBusinessService.toggleTokenActive(tokenId);
    }
    /**
     * ADMIN: Create new token
     */
    createToken(tokenData) {
        return this.tokenBusinessService.createToken(tokenData);
    }
    /**
     * ADMIN: Update token
     */
    updateToken(tokenId, tokenData) {
        return this.tokenBusinessService.updateToken(tokenId, tokenData);
    }
    /**
     * ADMIN: Set mainnet contract address
     */
    setMainnetContract(tokenId, contractAddress, chainId) {
        return this.tokenBusinessService.setMainnetContract(tokenId, contractAddress, chainId);
    }
    /**
     * ADMIN: Toggle token metadata status
     */
    toggleTokenMetadataStatus(metadataId) {
        return this.tokenBusinessService.toggleTokenMetadataStatus(metadataId);
    }
    /**
     * ADMIN: Create token type
     */
    createTokenType(tokenType) {
        return this.tokenBusinessService.createTokenType(tokenType);
    }
}

export { BaseTokenService as B, TokenSDK as T, getMetadataFromTokenUnitResponse as g };
//# sourceMappingURL=base-token-service-BysVghNg.js.map
